import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Briefcase, Plus, Search, Filter, Calendar, MapPin, Sparkles, ClipboardList, AlertTriangle, Loader2, AlertCircle, ChevronDown, Receipt, LinkIcon, CheckCircle } from 'lucide-react';
import GlassPanel from '../components/GlassPanel';
import EmptyState from '../components/EmptyState';
import { AIButton, AIBadge, AIPanel } from '../components/AIComponents';
import { usePagination } from '../hooks/usePagination';
import { CreateInvoiceFromJobModal, LineageBanner } from '../components/LineageComponents';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  'in-progress': 'active',
  'in_progress': 'active',
  'on-site': 'active',
  'on_site': 'active',
  'scheduled': 'pending',
  'booked': 'pending',
  'completed': 'completed',
  'paid': 'completed',
  'quoted': 'draft',
  'enquiry': 'draft',
  'quote-sent': 'pending',
  'quote_sent': 'pending',
  'quote-accepted': 'completed',
  'quote_accepted': 'completed',
  'quote-declined': 'overdue',
  'quote_declined': 'overdue'
};

export function Jobs() {
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const [selectedJob, setSelectedJob] = useState(null);
  const [showCreateInvoiceModal, setShowCreateInvoiceModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [jobLineages, setJobLineages] = useState({});
  
  const { 
    data: jobs, 
    loading, 
    loadingMore,
    error, 
    hasMore, 
    loadMore, 
    fetchData,
    pagination
  } = usePagination('jobs', { initialLimit: 25, orgAsQueryParam: true });

  useEffect(() => {
    fetchData(1, false);
  }, [fetchData]);

  // Fetch lineage info for jobs that might have it
  useEffect(() => {
    if (jobs.length > 0) {
      jobs.forEach(job => {
        if (job.source_quote_id && !jobLineages[job.id]) {
          fetch(`${API_URL}/api/lineage/verify/job/${job.id}`, {
            headers: getAuthHeaders()
          })
            .then(res => res.json())
            .then(lineage => {
              setJobLineages(prev => ({ ...prev, [job.id]: lineage }));
            })
            .catch(err => console.error('Error fetching lineage:', err));
        }
      });
    }
  }, [jobs, getAuthHeaders, jobLineages]);

  // Transform jobs for display
  const transformedJobs = jobs.map(job => ({
    id: job.id,
    title: job.title || 'Untitled Job',
    client: job.client_name || 'Client',
    address: job.address || 'No address specified',
    status: job.status?.toLowerCase().replace('_', '-') || 'enquiry',
    startDate: job.target_start_date ? new Date(job.target_start_date).toLocaleDateString('en-GB') : null,
    endDate: job.target_end_date ? new Date(job.target_end_date).toLocaleDateString('en-GB') : null,
    value: job.value || job.quote_amount_gross || job.quote_amount_net || 0,
    hasLineage: !!job.source_quote_id,
    sourceQuoteId: job.source_quote_id,
    rawData: job
  }));

  // Calculate stats
  const inProgressCount = transformedJobs.filter(j => 
    j.status === 'in-progress' || j.status === 'on-site' || j.status === 'booked'
  ).length;
  const scheduledCount = transformedJobs.filter(j => 
    j.status === 'scheduled' || j.status === 'quote-sent' || j.status === 'quote-accepted'
  ).length;
  const completedCount = transformedJobs.filter(j => 
    j.status === 'completed' || j.status === 'paid'
  ).length;

  // Jobs with lineage (can create invoice)
  const jobsWithLineage = transformedJobs.filter(j => j.hasLineage);

  const handleCreateInvoiceClick = (job) => {
    setSelectedJob(job.rawData);
    setShowCreateInvoiceModal(true);
  };

  const handleInvoiceCreated = (result) => {
    setSuccessMessage(`Invoice ${result.invoice_number} created! £${result.amount_gross.toFixed(2)}`);
    setTimeout(() => {
      navigate(`/invoices/${result.invoice_id}`);
    }, 1500);
  };

  return (
    <div className="page">
      {/* Header */}
      <div className="page-header">
        <div className="page-header-content">
          <h1>Job Life</h1>
          <p>Manage all your active and upcoming jobs</p>
        </div>
        <div className="page-header-actions">
          <Link to="/quotes" className="btn btn-primary" data-testid="create-job-link">
            <Plus size={16} />
            Create Job from Quote
          </Link>
        </div>
      </div>

      {/* Info Banner - How to create jobs */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(139, 92, 246, 0.1) 100%)',
        border: '1px solid rgba(99, 102, 241, 0.3)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--space-md)',
        marginBottom: 'var(--space-xl)',
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-md)'
      }}>
        <LinkIcon size={20} style={{ color: 'var(--accent-primary)', flexShrink: 0 }} />
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: '600', color: 'var(--text-primary)', fontSize: '0.875rem' }}>
            Quote → Job → Invoice Continuity
          </div>
          <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>
            Jobs are created from accepted Quotes. Line items are inherited automatically. No re-typing required.
          </div>
        </div>
        <Link to="/quotes" className="btn btn-secondary" style={{ whiteSpace: 'nowrap' }}>
          View Quotes
        </Link>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div style={{
          padding: 'var(--space-md)',
          background: 'var(--status-success-bg)',
          borderRadius: 'var(--radius-md)',
          marginBottom: 'var(--space-lg)',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-sm)'
        }}>
          <CheckCircle size={18} style={{ color: 'var(--status-success)' }} />
          <span style={{ color: 'var(--status-success)' }}>{successMessage}</span>
        </div>
      )}

      {/* AI Quick Actions */}
      <AIPanel 
        title="AI Job Assistant" 
        description="AI helps summarise and plan. It never changes job data automatically."
        className="mb-xl"
      >
        <div className="ai-actions-grid">
          <AIButton icon={Sparkles} tooltip="AI generation will be enabled soon">
            Summarise job status (AI)
          </AIButton>
          <AIButton icon={ClipboardList} tooltip="AI generation will be enabled soon">
            Create weekly action list
          </AIButton>
          <AIButton icon={AlertTriangle} tooltip="AI generation will be enabled soon">
            Predict delays
          </AIButton>
        </div>
      </AIPanel>

      {/* Error State */}
      {error && (
        <div style={{
          padding: 'var(--space-md)',
          background: 'var(--status-danger-bg)',
          borderRadius: 'var(--radius-md)',
          marginBottom: 'var(--space-lg)',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-sm)'
        }}>
          <AlertCircle size={18} style={{ color: 'var(--status-danger)' }} />
          <span style={{ color: 'var(--status-danger)' }}>{error}</span>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-3xl)' }}>
          <Loader2 size={32} style={{ animation: 'spin 1s linear infinite', color: 'var(--accent-primary)' }} />
        </div>
      )}

      {!loading && !error && (
        <>
          {/* Stats */}
          <div className="responsive-grid-4" style={{ marginBottom: 'var(--space-xl)' }}>
            <GlassPanel>
              <div style={{ fontSize: '1.75rem', fontWeight: '700', color: 'var(--accent-primary)' }}>
                {inProgressCount}
              </div>
              <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>In Progress</div>
            </GlassPanel>
            <GlassPanel>
              <div style={{ fontSize: '1.75rem', fontWeight: '700', color: 'var(--status-warning)' }}>
                {scheduledCount}
              </div>
              <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>Scheduled</div>
            </GlassPanel>
            <GlassPanel>
              <div style={{ fontSize: '1.75rem', fontWeight: '700', color: 'var(--status-success)' }}>
                {completedCount}
              </div>
              <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>Completed</div>
            </GlassPanel>
            <GlassPanel>
              <div style={{ fontSize: '1.75rem', fontWeight: '700', color: 'var(--text-secondary)' }}>
                {pagination.total}
              </div>
              <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>Total Jobs</div>
            </GlassPanel>
          </div>

          {/* Jobs Ready for Invoice */}
          {jobsWithLineage.length > 0 && (
            <GlassPanel 
              title="Ready to Invoice" 
              titleIcon={Receipt}
              className="mb-xl"
              style={{ background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.05) 0%, rgba(34, 197, 94, 0.1) 100%)' }}
            >
              <p style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', marginBottom: 'var(--space-md)' }}>
                These jobs have Quote lineage and can be invoiced. Invoice line items will retain full traceability.
              </p>
              <div className="list-container" data-testid="jobs-with-lineage-list">
                {jobsWithLineage.slice(0, 5).map((job) => (
                  <div 
                    key={job.id} 
                    className="glass-panel" 
                    style={{ 
                      padding: 'var(--space-md)',
                      marginBottom: 'var(--space-sm)',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                    data-testid={`job-with-lineage-${job.id}`}
                  >
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
                        <span style={{ fontWeight: '500', color: 'var(--text-primary)' }}>{job.title}</span>
                        <span style={{ 
                          fontSize: '0.6875rem', 
                          color: 'var(--accent-primary)',
                          background: 'rgba(99, 102, 241, 0.1)',
                          padding: '2px 6px',
                          borderRadius: 'var(--radius-sm)'
                        }}>
                          <LinkIcon size={10} style={{ verticalAlign: 'middle', marginRight: '3px' }} />
                          Quote #{job.sourceQuoteId?.slice(-4)}
                        </span>
                      </div>
                      <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>
                        {job.client} • £{job.value.toFixed(2)}
                      </div>
                    </div>
                    <button 
                      onClick={() => handleCreateInvoiceClick(job)}
                      className="btn btn-primary"
                      data-testid={`create-invoice-from-job-${job.id}`}
                      style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xs)' }}
                    >
                      <Receipt size={16} />
                      Create Invoice
                    </button>
                  </div>
                ))}
              </div>
            </GlassPanel>
          )}

          {/* Filters */}
          <div style={{ display: 'flex', gap: 'var(--space-md)', marginBottom: 'var(--space-lg)' }}>
            <div style={{ 
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-sm)',
              padding: 'var(--space-sm) var(--space-md)',
              background: 'var(--glass-bg)',
              border: '1px solid var(--glass-border)',
              borderRadius: 'var(--radius-md)'
            }}>
              <Search size={16} style={{ color: 'var(--text-dimmed)' }} />
              <input 
                type="text" 
                placeholder="Search jobs..."
                style={{ 
                  flex: 1,
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--text-primary)',
                  outline: 'none'
                }}
              />
            </div>
            <button className="btn btn-secondary">
              <Filter size={16} />
              Filter
            </button>
          </div>

          {/* Job List */}
          <GlassPanel title="All Jobs" titleIcon={Briefcase}>
            {transformedJobs.length > 0 ? (
              <>
                <div className="list-container" data-testid="jobs-list">
                  {transformedJobs.map((job) => (
                    <div key={job.id} className="glass-panel" style={{ marginBottom: 'var(--space-sm)', padding: 'var(--space-md)' }} data-testid={`job-item-${job.id}`}>
                      <Link to={`/jobs/${job.id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <div className="list-item-content">
                            <div className="list-item-title" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
                              {job.title}
                              <AIBadge text="AI" />
                              {job.hasLineage && (
                                <span style={{ 
                                  fontSize: '0.6875rem', 
                                  color: 'var(--accent-primary)',
                                  background: 'rgba(99, 102, 241, 0.1)',
                                  padding: '2px 6px',
                                  borderRadius: 'var(--radius-sm)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '3px'
                                }}>
                                  <LinkIcon size={10} />
                                  Linked
                                </span>
                              )}
                            </div>
                            <div className="list-item-subtitle">
                              {job.client}
                              <span style={{ margin: '0 8px' }}>•</span>
                              <MapPin size={12} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                              {job.address}
                            </div>
                          </div>
                          <div className="list-item-meta">
                            {job.startDate && (
                              <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>
                                <Calendar size={12} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                                {job.startDate}
                              </span>
                            )}
                            <span className={`status-badge ${statusColors[job.status] || 'draft'}`}>
                              {job.status.replace('-', ' ')}
                            </span>
                            {job.value > 0 && (
                              <span className="list-item-amount">£{job.value.toFixed(2)}</span>
                            )}
                          </div>
                        </div>
                      </Link>
                      
                      {/* Actions Row for jobs with lineage */}
                      {job.hasLineage && (
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          marginTop: 'var(--space-md)',
                          paddingTop: 'var(--space-md)',
                          borderTop: '1px solid var(--glass-border)'
                        }}>
                          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 'var(--space-xs)' }}>
                            <LinkIcon size={12} />
                            From Quote #{job.sourceQuoteId?.slice(-4)}
                          </div>
                          <button 
                            onClick={(e) => {
                              e.preventDefault();
                              handleCreateInvoiceClick(job);
                            }}
                            className="btn btn-secondary"
                            style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xs)' }}
                          >
                            <Receipt size={14} />
                            Create Invoice
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Load More Button */}
                {hasMore && (
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    marginTop: 'var(--space-lg)',
                    paddingTop: 'var(--space-lg)',
                    borderTop: '1px solid var(--glass-border)'
                  }}>
                    <button
                      onClick={loadMore}
                      disabled={loadingMore}
                      className="btn btn-secondary"
                      data-testid="load-more-jobs"
                      style={{ minWidth: '200px' }}
                    >
                      {loadingMore ? (
                        <>
                          <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                          Loading...
                        </>
                      ) : (
                        <>
                          <ChevronDown size={16} />
                          Load More ({pagination.total - transformedJobs.length} remaining)
                        </>
                      )}
                    </button>
                  </div>
                )}

                {/* Pagination Info */}
                <div style={{ 
                  textAlign: 'center', 
                  marginTop: 'var(--space-md)',
                  fontSize: '0.75rem',
                  color: 'var(--text-muted)'
                }}>
                  Showing {transformedJobs.length} of {pagination.total} jobs
                </div>
              </>
            ) : (
              <EmptyState 
                icon={Briefcase}
                title="No jobs yet"
                description="Create a job from an accepted quote to get started"
              />
            )}
          </GlassPanel>
        </>
      )}

      {/* Create Invoice Modal */}
      <CreateInvoiceFromJobModal
        isOpen={showCreateInvoiceModal}
        onClose={() => {
          setShowCreateInvoiceModal(false);
          setSelectedJob(null);
        }}
        job={selectedJob}
        onSuccess={handleInvoiceCreated}
      />
    </div>
  );
}

export default Jobs;
